import { Card, CardContent } from "@/components/ui/card";
import { Building, Calendar } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type EmploymentHistory = {
  id: string;
  position: string | null;
  start_date: string | null;
  end_date: string | null;
  is_current: boolean | null;
  company_name: string | null;
  clients?: {
    name: string;
  };
};

interface CareerHistoryProps {
  personId: string;
}

export function CareerHistory({ personId }: CareerHistoryProps) {
  const { data: employmentHistory = [], isLoading } = useQuery({
    queryKey: ["employment-history", personId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("employment_history")
        .select(`
          *,
          clients (
            name
          )
        `)
        .eq("person_id", personId)
        .order("start_date", { ascending: false });
      
      if (error) throw error;
      return data as EmploymentHistory[];
    },
    enabled: !!personId
  });

  if (isLoading) {
    return <div className="text-center py-4">Loading career history...</div>;
  }

  if (employmentHistory.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No career history available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {employmentHistory.map((job) => (
        <Card key={job.id}>
          <CardContent className="p-6">
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <Building className="h-4 w-4 text-primary" />
                  <h3 className="font-semibold">{job.position}</h3>
                  {job.is_current && (
                    <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded">
                      Current
                    </span>
                  )}
                </div>
                <div className="text-muted-foreground mb-3">
                  {job.clients?.name || job.company_name}
                </div>
                <div className="flex items-center space-x-2 text-sm text-muted-foreground">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {job.start_date ? new Date(job.start_date).toLocaleDateString() : "Unknown start"} - 
                    {job.is_current ? " Present" : job.end_date ? ` ${new Date(job.end_date).toLocaleDateString()}` : " Unknown end"}
                  </span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}